/**
 * Error routing for the Bulk Upload (.csv) import flow (EUD-414).
 *
 * Only deliberate, user-friendly validation messages are shown to the user;
 * anything else (API rejections, native runtime errors like an unexpected
 * response shape) falls back to a friendly message and is logged, so raw
 * technical text is never surfaced.
 */

/** Tags the deliberate, user-friendly validation messages thrown in the flow. */
export class ImportValidationError extends Error {}

export type ImportMode = 'newUpload' | 'finishImport';
export type ImportStep = 'upload' | 'associate';

export interface ImportErrorRoute {
  /** i18n key for the friendly fallback message (used for non-validation errors). */
  fallbackKey: string;
  /** Whether the message belongs in the association error slot vs the upload one. */
  isAssociationError: boolean;
}

/**
 * Decides how to present an import failure that is NOT an
 * {@link ImportValidationError}: which friendly fallback key to show and which
 * error slot it belongs in, based on the mode and the step that failed.
 *
 * - Finishing an existing import is always an association-step failure.
 * - A new upload fails either during the upload step or, once a valid UploadId
 *   is returned, during the association step.
 */
const resolveFallbackKey = (
  importMode: ImportMode,
  step: ImportStep,
): string => {
  if (importMode === 'finishImport') {
    return 'addTransactionModal.finishImportFailed';
  }
  if (step === 'associate') {
    return 'addTransactionModal.associationFailed';
  }
  return 'addTransactionModal.processingFailed';
};

export const resolveImportError = (
  importMode: ImportMode,
  step: ImportStep,
): ImportErrorRoute => ({
  isAssociationError: importMode === 'finishImport' || step === 'associate',
  fallbackKey: resolveFallbackKey(importMode, step),
});
